import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import JsonLd from "@/components/feature/JsonLd";
import ArticleCard from "@/components/feature/ArticleCard";
import { usePageSeo } from "@/hooks/usePageSeo";
import { SITE } from "@/lib/site";
import { formatCount } from "@/lib/format";
import { categories } from "@/mocks/categories";
import { articles } from "@/mocks/articles";

const PAGE_SIZE = 6;

export default function CategoryPage() {
  const { slug = "" } = useParams();
  const category = categories.find((item) => item.slug === slug);
  const [keyword, setKeyword] = useState("");
  const [sort, setSort] = useState<"latest" | "hot">("latest");
  const [visible, setVisible] = useState(PAGE_SIZE);

  const baseList = useMemo(
    () => articles.filter((item) => item.categorySlug === slug),
    [slug],
  );

  const list = useMemo(() => {
    const kw = keyword.trim();
    const filtered = kw
      ? baseList.filter(
          (item) =>
            item.title.includes(kw) ||
            item.excerpt.includes(kw) ||
            item.tags.some((tag) => tag.includes(kw)),
        )
      : baseList;
    return [...filtered].sort((a, b) =>
      sort === "hot" ? b.views - a.views : new Date(b.date).getTime() - new Date(a.date).getTime(),
    );
  }, [baseList, keyword, sort]);

  const sideHot = useMemo(() => [...articles].sort((a, b) => b.views - a.views).slice(0, 5), []);

  usePageSeo({
    title: category ? `${category.name} - 今日爆料合集` : "分类未找到",
    description: category
      ? `${category.name}：${category.description} 每日更新爆料专区${category.name}合集，帮你快速掌握最新猛料。`
      : "该分类暂未找到，请返回爆料专区首页浏览最新爆料。",
    keywords: category ? `${category.name},爆料专区,今日爆料,吃瓜合集` : SITE.keywords,
    path: `/category/${slug}`,
    type: "website",
  });

  const jsonLd = category
    ? [
        {
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: `${category.name} - 爆料专区`,
          description: category.description,
          url: `${SITE.domain}/category/${slug}`,
        },
        {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "首页", item: SITE.domain },
            { "@type": "ListItem", position: 2, name: category.name, item: `${SITE.domain}/category/${slug}` },
          ],
        },
      ]
    : [];

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      {jsonLd.length > 0 && <JsonLd data={jsonLd} />}
      <SiteHeader />
      <main className="flex-1">
        <div className="border-b border-background-200 bg-background-100">
          <div className="mx-auto w-full max-w-7xl px-4 py-8 md:px-8 md:py-10">
            <nav aria-label="面包屑" className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
              <Link to="/" className="transition-colors hover:text-primary-600">
                首页
              </Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-800">{category ? category.name : "未知分类"}</span>
            </nav>

            {category ? (
              <>
                <div className="mt-4 flex items-center gap-3">
                  <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-500 text-background-50">
                    <i className={`${category.icon} text-2xl`}></i>
                  </span>
                  <h1 className="font-heading text-2xl tracking-wide text-foreground-950 md:text-4xl">
                    {category.name}
                  </h1>
                </div>
                <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">{category.description}</p>
              </>
            ) : (
              <h1 className="mt-4 font-heading text-2xl tracking-wide text-foreground-950 md:text-4xl">
                没有找到这个分类
              </h1>
            )}
          </div>
        </div>

        {category ? (
          <div className="mx-auto w-full max-w-7xl px-4 py-8 md:px-8 md:py-10">
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_300px]">
              <div>
                <div className="flex flex-col gap-3 border-b border-background-200 pb-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="relative flex-1 sm:max-w-xs">
                    <span className="pointer-events-none absolute left-3 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-400">
                      <i className="ri-search-line text-sm"></i>
                    </span>
                    <input
                      type="search"
                      value={keyword}
                      onChange={(event) => {
                        setKeyword(event.target.value);
                        setVisible(PAGE_SIZE);
                      }}
                      placeholder={`在${category.name}里搜索`}
                      className="h-10 w-full rounded-md border border-background-200 bg-background-50 pl-9 pr-3 text-sm outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                    />
                  </div>
                  <div className="flex items-center gap-1 rounded-full bg-background-100 p-1">
                    {[
                      { key: "latest", label: "最新" },
                      { key: "hot", label: "最热" },
                    ].map((item) => (
                      <button
                        key={item.key}
                        type="button"
                        onClick={() => setSort(item.key as "latest" | "hot")}
                        className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                          sort === item.key ? "bg-primary-500 text-background-50" : "text-foreground-700"
                        }`}
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                </div>

                {list.length > 0 ? (
                  <>
                    <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2">
                      {list.slice(0, visible).map((article) => (
                        <ArticleCard key={article.id} article={article} />
                      ))}
                    </div>
                    {visible < list.length && (
                      <div className="mt-8 flex justify-center">
                        <button
                          type="button"
                          onClick={() => setVisible((prev) => prev + PAGE_SIZE)}
                          className="flex h-11 cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-6 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100"
                        >
                          加载更多
                          <i className="ri-arrow-down-line"></i>
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <p className="mt-10 rounded-lg border border-dashed border-background-300 py-14 text-center text-sm text-foreground-500">
                    没有匹配的爆料，换个关键词试试。
                  </p>
                )}
              </div>

              <aside className="space-y-6">
                <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <h2 className="flex items-center gap-2 font-heading text-lg tracking-wide text-foreground-950">
                    <span className="flex h-6 w-6 items-center justify-center rounded-md bg-accent-500 text-primary-950">
                      <i className="ri-fire-fill text-sm"></i>
                    </span>
                    全站热榜
                  </h2>
                  <ol className="mt-4 space-y-3">
                    {sideHot.map((item, index) => (
                      <li key={item.id}>
                        <Link to={`/article/${item.id}`} className="group flex gap-3">
                          <span className="w-4 shrink-0 font-heading text-lg text-primary-500">{index + 1}</span>
                          <span className="min-w-0">
                            <span className="line-clamp-2 text-sm leading-snug text-foreground-800 transition-colors group-hover:text-primary-600">
                              {item.title}
                            </span>
                            <span className="mt-1 block text-xs text-foreground-400">
                              {formatCount(item.views)} 次围观
                            </span>
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ol>
                </div>

                <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <h2 className="font-heading text-lg tracking-wide text-foreground-950">换个分类看看</h2>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {categories.map((cat) => (
                      <Link
                        key={cat.slug}
                        to={`/category/${cat.slug}`}
                        className={`rounded-full px-3 py-1.5 text-xs transition-colors ${
                          cat.slug === slug
                            ? "bg-primary-500 text-background-50"
                            : "bg-background-100 text-foreground-700 hover:bg-background-200"
                        }`}
                      >
                        {cat.name}
                      </Link>
                    ))}
                  </div>
                </div>
              </aside>
            </div>
          </div>
        ) : (
          <div className="mx-auto w-full max-w-7xl px-4 py-16 text-center md:px-8">
            <p className="text-sm text-foreground-600">这个分类可能被移动了，去首页看看今天有什么新料？</p>
            <Link
              to="/"
              className="mt-6 inline-flex h-11 items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              返回首页
            </Link>
          </div>
        )}
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}